import React from 'react';
import { toolkitService } from '../services/toolkitService';
import { buildTimingModel, resolveScheduleKey } from './plantingTimingUtils';

export function PlantingTimingStep({
  answers,
  cropName = 'oats',
  latitude = null,
  longitude = null,
  usdaZoneCode = '',
  soilDrainage = '',
  frostSource = '',
  frostLastLabel = '',
  frostFirstLabel = '',
}) {
  const [forecastPayload, setForecastPayload] = React.useState(null);
  const [forecastStatus, setForecastStatus] = React.useState('stub');
  const seqRef = React.useRef(0);

  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const season = resolveScheduleKey(answers);

  React.useEffect(() => {
    if (!hasCoords) {
      setForecastPayload(null);
      setForecastStatus('missing');
      return undefined;
    }

    const seq = seqRef.current + 1;
    seqRef.current = seq;
    let cancelled = false;
    setForecastStatus('loading');
    setForecastPayload(null);

    (async () => {
      try {
        // Still uses planting-forecast for window + frost (weather days ignored in window mode).
        const data = await toolkitService.getPlantingForecast({
          latitude: lat,
          longitude: lng,
          season,
          usdaZoneCode,
          soilDrainage,
          frostSource,
          lastFrostLabel: frostLastLabel,
          firstFrostLabel: frostFirstLabel,
          days: 10,
        });
        if (cancelled || seqRef.current !== seq) return;
        setForecastPayload(data);
        const hasWindow = Boolean(data?.window?.start && data?.window?.end);
        setForecastStatus(hasWindow ? 'window' : 'unavailable');
      } catch (err) {
        console.error('Planting window lookup failed', err);
        if (cancelled || seqRef.current !== seq) return;
        setForecastPayload(null);
        setForecastStatus('unavailable');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasCoords, lat, lng, season, usdaZoneCode, soilDrainage, frostSource, frostLastLabel, frostFirstLabel]);

  const model = buildTimingModel({
    answers,
    cropName,
    forecastPayload,
    forecastStatus,
    mode: 'window',
  });

  return (
    <section className="plant-timing" data-testid="plant-timing-step">
      <div className="plant-timing-hero">
        <div className="plant-timing-hero__copy">
          <div className="plant-timing-hero__kicker">PLANTING WINDOW</div>
          <div className="plant-timing-hero__day" data-testid="plant-timing-best-day">
            {model.bestDay}
          </div>
          <p className="plant-timing-hero__why" data-testid="plant-timing-best-why">
            {model.bestWhy}
          </p>
        </div>
        <div className="plant-timing-hero__chips">
          <span
            className="plant-timing-chip plant-timing-chip--year"
            data-testid="plant-timing-year-chip"
          >
            {model.yearChip}
          </span>
          <span className="plant-timing-chip plant-timing-chip--window">
            WINDOW {model.windowLabel}
          </span>
        </div>
      </div>

      <div className="plant-timing__grid">
        <div className="plant-timing-card">
          <div className="plant-timing-card__head">
            <span>{model.calMonth}</span>
            <span className="plant-timing-card__accent">PLANTING WINDOW</span>
          </div>
          <div className="plant-timing-cal">
            <div className="plant-timing-cal__grid" role="grid" aria-label={model.calMonth}>
              {model.dowHeads.map((d, i) => (
                <div key={`h-${i}`} className="plant-timing-cal__dow" role="columnheader">
                  {d.label}
                </div>
              ))}
              {model.calCells.map((cell, i) => (
                <div
                  key={`c-${i}`}
                  className={`plant-timing-cal__cell${cell.isBest ? ' is-best' : ''}${cell.empty ? ' is-empty' : ''}`}
                  style={{
                    background: cell.bg,
                    border: cell.border,
                    color: cell.fg,
                    fontSize: cell.size,
                  }}
                  role="gridcell"
                >
                  {cell.day}
                  {cell.mark ? (
                    <span style={{ color: cell.markFg }}>{cell.mark}</span>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="plant-timing-legend">
              {model.calLegend.map((item) => (
                <span key={item.label} className="plant-timing-legend__item">
                  <span style={{ background: item.color }} aria-hidden="true" />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="plant-timing-side">
          <div className="plant-timing-card" data-testid="plant-timing-plant-week-note">
            <div className="plant-timing-card__head">
              <span>PLANT THIS WEEK</span>
              <span className="plant-timing-card__accent">ON MY PLANS</span>
            </div>
            <div className="plant-timing-rain">
              <p className="plant-timing-rain__note">
                {model.plantWeekNote
                  || 'Near-term work days will live on My Plans, grouped by season — not in this planning step.'}
              </p>
            </div>
          </div>

          <div className="plant-timing-card">
            <div className="plant-timing-card__head">
              <span>FROST BOUNDS</span>
              <span className="plant-timing-card__accent">{model.rainDelta}</span>
            </div>
            <div className="plant-timing-rain">
              {model.rainBars.map((bar) => (
                <div key={bar.label} className="plant-timing-rain__row">
                  <div className="plant-timing-rain__labels">
                    <span>{bar.label}</span>
                    <span className="plant-timing-rain__value">{bar.value}</span>
                  </div>
                  <div className="plant-timing-rain__bar" aria-hidden="true">
                    <div style={{ width: bar.pct, background: bar.fill }} />
                  </div>
                </div>
              ))}
              <p className="plant-timing-rain__note">{model.rainNote}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
